import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '../utils/cn';

export interface RatingFieldOption {
  id: string;
  score: number;
  label?: string;
  value: string;
}

interface RatingFieldInputProps {
  options: RatingFieldOption[];
  value?: string | number;
  onChange: (value: string) => void;
  error?: string;
  getSelectedScore?: (
    value: string | number | undefined | null,
    options: RatingFieldOption[]
  ) => number | undefined;
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <Star
      className={cn(
        'h-6 w-6 shrink-0',
        filled ? 'fill-primary text-primary' : 'fill-none text-gray-300'
      )}
    />
  );
}

function CumulativeStars({
  count,
  filledUpTo,
}: {
  count: number;
  filledUpTo: number;
}) {
  return (
    <div className="flex shrink-0 gap-0.5">
      {Array.from({ length: count }).map((_, index) => (
        <StarIcon key={index} filled={index + 1 <= filledUpTo} />
      ))}
    </div>
  );
}

const MAX_HORIZONTAL_OPTIONS = 5;
const LABEL_LINE_HEIGHT_PX = 16;
const LABEL_MAX_LINES = 2;
const LABEL_MAX_HEIGHT_PX = LABEL_LINE_HEIGHT_PX * LABEL_MAX_LINES;

function RatingLabel({
  label,
  isSelected,
  measure,
}: {
  label: string;
  isSelected: boolean;
  measure?: boolean;
}) {
  return (
    <span
      data-rating-label={measure ? '' : undefined}
      className={cn(
        'block max-w-full overflow-hidden px-0.5 text-center text-xs leading-4',
        '[overflow-wrap:break-word] [word-break:normal]',
        isSelected ? 'text-primary' : 'text-gray-700'
      )}
      style={{ height: LABEL_MAX_HEIGHT_PX }}
    >
      {label}
    </span>
  );
}

function HorizontalOptionButton({
  option,
  isSelected,
  isFilled,
  onChange,
  stretch,
  measure,
}: {
  option: RatingFieldOption;
  isSelected: boolean;
  isFilled: boolean;
  onChange: (value: string) => void;
  stretch: boolean;
  measure?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(option.value)}
      tabIndex={measure ? -1 : undefined}
      className={cn(
        'rating-option flex flex-col items-center gap-2 rounded-lg px-1 py-1.5 transition-colors focus:outline-none',
        stretch ? 'min-w-0 flex-1' : 'min-w-0 shrink-0',
        isSelected && 'rating-option--selected relative z-[1]'
      )}
    >
      <StarIcon filled={isFilled} />
      {option.label?.trim() ? (
        <RatingLabel
          label={option.label.trim()}
          isSelected={isSelected}
          measure={measure}
        />
      ) : null}
    </button>
  );
}

export function RatingFieldInput({
  options,
  value,
  onChange,
  error,
  getSelectedScore,
}: RatingFieldInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<'horizontal' | 'stacked'>('horizontal');

  const selectedScore = getSelectedScore
    ? getSelectedScore(value, options)
    : options.find((option) => option.value === String(value ?? ''))?.score;

  const hasLabels = options.some((option) => option.label?.trim());

  useEffect(() => {
    if (!hasLabels || options.length <= MAX_HORIZONTAL_OPTIONS) {
      setLayout('horizontal');
      return;
    }

    const containerEl = containerRef.current;
    const measureEl = measureRef.current;
    if (!containerEl || !measureEl) {
      return;
    }

    const checkLayout = () => {
      if (containerEl.clientWidth === 0) {
        return;
      }

      const labelEls = measureEl.querySelectorAll('[data-rating-label]');
      const labelOverflows = Array.from(labelEls).some(
        (el) => el.scrollHeight > el.clientHeight + 1
      );

      setLayout(labelOverflows ? 'stacked' : 'horizontal');
    };

    checkLayout();

    const observer = new ResizeObserver(checkLayout);
    observer.observe(containerEl);

    return () => observer.disconnect();
  }, [options, hasLabels]);

  if (options.length === 0) {
    return null;
  }

  const horizontalRowClassName = 'flex gap-1 w-full justify-between flex-wrap';

  return (
    <div ref={containerRef} className="rating-field relative w-full">
      {hasLabels && options.length > MAX_HORIZONTAL_OPTIONS ? (
        <div
          aria-hidden
          className="pointer-events-none invisible absolute left-0 top-0 -z-10 w-full opacity-0"
        >
          <div ref={measureRef} className={horizontalRowClassName}>
            {options.map((option) => (
              <HorizontalOptionButton
                key={`measure-${option.id}`}
                option={option}
                isSelected={false}
                isFilled={false}
                onChange={() => undefined}
                stretch
                measure
              />
            ))}
          </div>
        </div>
      ) : null}

      {layout === 'horizontal' ? (
        <div className={horizontalRowClassName}>
          {options.map((option) => {
            const isSelected = option.score === selectedScore;
            const isFilled =
              selectedScore != null && option.score <= selectedScore;

            return (
              <HorizontalOptionButton
                key={option.id}
                option={option}
                isSelected={isSelected}
                isFilled={isFilled}
                onChange={onChange}
                stretch
              />
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {options.map((option) => {
            const isSelected = option.score === selectedScore;

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onChange(option.value)}
                className={cn(
                  'rating-option relative flex items-center gap-2 rounded-lg py-1 text-left transition-colors focus:outline-none',
                  isSelected && 'rating-option--selected z-[1]'
                )}
              >
                <CumulativeStars
                  count={options.length}
                  filledUpTo={option.score}
                />
                {option.label?.trim() ? (
                  <span
                    className={cn(
                      'text-sm text-gray-700',
                      isSelected && 'text-primary'
                    )}
                  >
                    {option.label.trim()}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {error ? <p className="form-error-message mt-1">{error}</p> : null}
    </div>
  );
}
